import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalHRPDFExport from '../../components/legal-hr/LegalHRPDFExport';
import toast from 'react-hot-toast';
import { anthropicService } from '../../services/anthropicService';
import { supabase } from '../../lib/supabase';
import { legalHrStorageService, SavedLegalHrDocument } from '../../services/legalHrStorageService';

// Define supported countries for employee handbook
const supportedCountries = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Define business types for dropdown
const businessTypes = [
  'Technology Company',
  'Retail Business',
  'Professional Services',
  'Healthcare Organization',
  'Manufacturing Company',
  'Hospitality Business',
  'Educational Institution',
  'Non-profit Organization',
  'Financial Services',
  'Creative Agency',
];

// Define handbook sections
const handbookSections = [
  { id: 'welcome', label: 'Welcome & Introduction', example: 'Company overview, mission, values', required: true },
  { id: 'employment', label: 'Employment Policies', example: 'Employment status, probation, termination', required: true },
  { id: 'conduct', label: 'Code of Conduct', example: 'Professional behavior, ethics, dress code', required: true },
  { id: 'attendance', label: 'Attendance & Leave', example: 'Working hours, time off, holidays', defaultSelected: true },
  { id: 'compensation', label: 'Compensation & Benefits', example: 'Pay, bonuses, health benefits, retirement', defaultSelected: true },
  { id: 'performance', label: 'Performance Management', example: 'Reviews, promotions, development', defaultSelected: true },
  { id: 'health_safety', label: 'Health & Safety', example: 'Workplace safety, accident procedures', defaultSelected: true },
  { id: 'it_security', label: 'IT & Security Policies', example: 'Computer usage, data protection, passwords', defaultSelected: false },
  { id: 'social_media', label: 'Social Media Policy', example: 'Guidelines for representing the company online', defaultSelected: false },
  { id: 'grievance', label: 'Grievance Procedures', example: 'How to report issues, escalation process', defaultSelected: false },
  { id: 'disciplinary', label: 'Disciplinary Procedures', example: 'Progressive discipline, misconduct consequences', defaultSelected: false },
  { id: 'equal_opportunity', label: 'Equal Opportunity & Diversity', example: 'Anti-discrimination, inclusion policies', defaultSelected: true },
];

const EmployeeHandbookCreator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [handbookGenerated, setHandbookGenerated] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [selectedSections, setSelectedSections] = useState<string[]>(
    handbookSections.filter(section => section.defaultSelected || section.required).map(section => section.id)
  );
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [handbookText, setHandbookText] = useState('');
  const [latestDocument, setLatestDocument] = useState<SavedLegalHrDocument | null>(null);
  const [remoteWork, setRemoteWork] = useState(false);
  const [hasHRDepartment, setHasHRDepartment] = useState(false);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const handleSectionChange = (sectionId: string) => {
    // Don't allow deselection of required sections
    if (handbookSections.find(section => section.id === sectionId)?.required) {
      return;
    }
    
    if (selectedSections.includes(sectionId)) {
      setSelectedSections(selectedSections.filter(item => item !== sectionId));
    } else {
      setSelectedSections([...selectedSections, sectionId]);
    }
  };

  const generateHandbook = async () => {
    // Validation
    if (!companyName) {
      toast.error('Please enter your company name');
      return;
    }
    
    if (!businessType) {
      toast.error('Please select your business type');
      return;
    }
    
    if (!contactEmail) {
      toast.error('Please enter a contact email');
      return;
    }
    
    if (!employeeCount) {
      toast.error('Please enter your employee count');
      return;
    }

    setGenerating(true);
    
    try {
      // Get the country name from the selected country code
      const selectedCountryData = supportedCountries.find(country => country.code === selectedCountry);
      const countryName = selectedCountryData ? selectedCountryData.name : 'United Kingdom';
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Format the selected handbook sections into a readable list
      const selectedHandbookSections = handbookSections
        .filter(section => selectedSections.includes(section.id))
        .map(section => section.label)
        .join(', ');

      // Create a comprehensive prompt for Anthropic
      const prompt = `
Generate a comprehensive employee handbook for ${companyName}, a ${businessType} with approximately ${employeeCount} employees, primarily operating in ${countryName}.

The handbook should include the following sections: ${selectedHandbookSections}

Important company details:
- Primary jurisdiction: ${countryName}
- Contact email for HR inquiries: ${contactEmail}
- Effective date for the handbook: ${effectiveDate}
- ${remoteWork ? 'The company has remote workers and needs relevant policies.' : 'The company does not have remote workers.'}
- ${hasHRDepartment ? 'The company has a dedicated HR department.' : 'The company does not have a dedicated HR department.'}

Please format the employee handbook in markdown, with appropriate sections, headings, and comprehensive coverage of all required elements for ${countryName}.

Make sure to comply with employment laws specific to ${countryName} such as ${
  selectedCountry === 'GB' ? 'Employment Rights Act, Equality Act, Working Time Regulations, etc.' : 
  selectedCountry === 'US' ? 'Fair Labor Standards Act, Family and Medical Leave Act, ADA, etc.' : 
  selectedCountry === 'EU' ? 'Working Time Directive, GDPR as it relates to employee data, etc.' : 
  selectedCountry === 'CA' ? 'Canada Labour Code, provincial employment standards, etc.' : 
  selectedCountry === 'AU' ? 'Fair Work Act, Workplace Health and Safety regulations, etc.' : 
  'relevant employment legislation'
}.

The handbook should be written in a clear, professional tone and formatted for easy reading by employees. Include appropriate disclaimers stating that the handbook does not constitute a contract and can be updated by the company.
`;

      // Make the actual API call to Anthropic through our service
      const response = await anthropicService.generateText(prompt);
      
      setHandbookText(response);
      setLatestDocument(null);
      setHandbookGenerated(true);
      toast.success('Employee Handbook generated successfully!');
    } catch (error) {
      console.error('Error generating employee handbook:', error);
      toast.error('Failed to generate employee handbook. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (handbookText) {
      navigator.clipboard.writeText(handbookText)
        .then(() => toast.success('Employee handbook copied to clipboard'))
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy text');
        });
    } else {
      toast.error('No content to copy');
    }
  };

  const handlePdfExportComplete = async ({ blob }: { filename: string; blob: Blob }) => {
    if (!user?.id) {
      toast.error('You must be logged in to save the handbook');
      return;
    }

    try {
      const saved = await legalHrStorageService.saveLegalHrDocument({
        userId: user.id,
        docType: 'employee-handbook',
        blob,
        baseName: companyName || 'employee-handbook',
        effectiveDate,
        metadata: {
          companyName,
          businessType,
          contactEmail,
          employeeCount,
          jurisdiction: selectedCountry,
          remoteWork,
          hasHRDepartment,
          selectedSections,
        },
        tags: [
          `jurisdiction:${selectedCountry}`,
          remoteWork ? 'remote-work:yes' : 'remote-work:no',
          `hr-department:${hasHRDepartment ? 'yes' : 'no'}`,
        ],
      });

      setLatestDocument(saved);
      toast.success(`Employee Handbook saved to workspace as version v${saved.version}`);
    } catch (error) {
      console.error('Failed to upload Employee Handbook PDF', error);
      toast.error('Failed to save Employee Handbook to storage');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600">
            Employee Handbook Creator
          </h1>
          <p className="mt-3 text-xl text-gray-600 max-w-2xl mx-auto">
            Create a comprehensive employee handbook tailored to your organization and compliant with your jurisdiction.
          </p>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {!handbookGenerated ? (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column - Company Info */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name*
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Your Company Ltd"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type*
                      </label>
                      <select
                        id="businessType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      >
                        <option value="">Select a business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="employeeCount" className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Employees*
                      </label>
                      <select
                        id="employeeCount"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        value={employeeCount}
                        onChange={(e) => setEmployeeCount(e.target.value)}
                      >
                        <option value="">Select employee count</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        HR Contact Email*
                      </label>
                      <input
                        type="email"
                        id="contactEmail"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="hr@example.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Jurisdiction*
                      </label>
                      <select
                        id="country"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                      >
                        {supportedCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective Date
                      </label>
                      <input
                        type="date"
                        id="effectiveDate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>

                    <div className="space-y-3 mt-4">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="remoteWork"
                            type="checkbox"
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            checked={remoteWork}
                            onChange={() => setRemoteWork(!remoteWork)}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="remoteWork" className="font-medium text-gray-700">
                            Remote Work Policies
                          </label>
                          <p className="text-gray-500">Include remote work guidelines and policies</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="hasHRDepartment"
                            type="checkbox"
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            checked={hasHRDepartment}
                            onChange={() => setHasHRDepartment(!hasHRDepartment)}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="hasHRDepartment" className="font-medium text-gray-700">
                            Dedicated HR Department
                          </label>
                          <p className="text-gray-500">Company has HR personnel/department</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column - Handbook Sections */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Handbook Sections</h2>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select sections to include in your handbook:
                    </label>
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      {handbookSections.map((section) => (
                        <div key={section.id} className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id={section.id}
                              type="checkbox"
                              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              checked={selectedSections.includes(section.id)}
                              onChange={() => handleSectionChange(section.id)}
                              disabled={section.required}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor={section.id} className="font-medium text-gray-700 flex items-center">
                              {section.label}
                              {section.required && (
                                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                  Required
                                </span>
                              )}
                            </label>
                            <p className="text-gray-500">{section.example}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={generateHandbook}
                  disabled={generating}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Generate Employee Handbook'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="mb-6 flex justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generated Employee Handbook</h2>
                <div className="space-x-2">
                  <button 
                    onClick={handleCopyToClipboard}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Copy to Clipboard
                  </button>
                  {handbookText && (
                    <LegalHRPDFExport 
                      contentRef={contentRef} 
                      filename="employee-handbook.pdf"
                      title={companyName ? `${companyName} - Employee Handbook` : "Employee Handbook"}
                      documentType="Employee Handbook"
                      onExportComplete={handlePdfExportComplete}
                    />
                  )}
                  <button 
                    onClick={() => setHandbookGenerated(false)}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              {/* Display the generated handbook in a nicely formatted way */}
              <div className="bg-white p-6 rounded-lg shadow-md" ref={contentRef}>
                <div dangerouslySetInnerHTML={{ __html: handbookText }} />
              </div>
              
              <div className="mt-8 text-center">
                {latestDocument?.signedUrl && (
                  <div className="mb-4">
                    <a
                      href={latestDocument.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 underline"
                    >
                      View saved Employee Handbook (v{latestDocument.version})
                    </a>
                  </div>
                )}
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mb-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Legal Disclaimer:</strong> This employee handbook is generated by AI and should be reviewed by a qualified HR professional and legal counsel before use. Employment laws vary by jurisdiction and may change over time. The generated handbook may not cover all legal requirements specific to your business or jurisdiction.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setHandbookGenerated(false);
                    window.scrollTo(0, 0);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Generate Another Handbook
                </button>
              </div>
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 border-t border-yellow-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Legal Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This generator provides a template only and is not a substitute for professional HR and legal advice. 
                    The generated handbook should be reviewed by qualified professionals before implementation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeHandbookCreator;
